import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { GlobalContext } from '../context/globalContext';
import { CPPDDashboardData, CPPDFilterParams } from '../types/dashboard/cppd.types';
import cppdService from '../services/dashboard/cppd.service';

const useCPPDDashboard = () => {
  const [data, setData] = useState<CPPDDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const globalContext = useContext(GlobalContext);
  const currentClienteId = globalContext?.currentCliente?._id;
  
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastClienteIdRef = useRef<string | null>(null);

  // Função para obter cliente ID de forma reativa
  const getClienteId = useCallback((): string | null => {
    // Priorizar o contexto global
    if (currentClienteId) return currentClienteId;
    
    try {
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        const parsed = JSON.parse(userInfo);
        if (parsed?.cliente_id) return parsed.cliente_id;
      }

      const currentCliente = localStorage.getItem('currentCliente');
      if (currentCliente) {
        const parsed = JSON.parse(currentCliente);
        if (parsed?._id) return parsed._id;
      }

      return null;
    } catch {
      return null;
    }
  }, [currentClienteId]);

  const fetchData = useCallback(async (filters?: CPPDFilterParams) => {
    const clienteId = getClienteId();
    
    if (!clienteId) {
      setError('Cliente não selecionado');
      setLoading(false);
      return;
    }

    // Cancelar requisição anterior se existir
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Criar novo controller para esta requisição
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const finalFilters: CPPDFilterParams = {
        cliente_id: clienteId,
        ...filters
      };

      // Garantir que as datas estejam no formato correto
      if (finalFilters.dataInicio) {
        if (typeof finalFilters.dataInicio === 'string' && !finalFilters.dataInicio.includes('T')) {
          finalFilters.dataInicio = finalFilters.dataInicio;
        }
      }
      
      if (finalFilters.dataFim) {
        if (typeof finalFilters.dataFim === 'string' && !finalFilters.dataFim.includes('T')) {
          finalFilters.dataFim = finalFilters.dataFim;
        }
      }

      const result = await cppdService.getDashboardData(finalFilters);
      
      // Verificar se a requisição não foi cancelada
      if (!abortControllerRef.current?.signal.aborted) {
        setData(result);
        setIsInitialized(true);
        lastClienteIdRef.current = clienteId;
      }
    } catch (error) {
      if (!abortControllerRef.current?.signal.aborted) {
        const errorMessage = error instanceof Error ? error.message : 'Erro ao carregar dados';
        console.error('Erro no useCPPDDashboard:', errorMessage, error);
        setError(errorMessage);
      }
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [getClienteId]);

  // Detectar mudança de cliente e refazer fetch
  useEffect(() => {
    const clienteId = getClienteId();
    
    // Se mudou o cliente, limpar dados e refazer fetch
    if (clienteId && clienteId !== lastClienteIdRef.current) {
      setData(null);
      setIsInitialized(false);
      fetchData();
    } else if (!clienteId) {
      setData(null);
      setError('Cliente não selecionado');
      setIsInitialized(true);
    }
  }, [currentClienteId, fetchData, getClienteId]);

  // Fetch inicial apenas se não foi inicializado
  useEffect(() => {
    if (!isInitialized && !loading) {
      fetchData();
    }
  }, [isInitialized, loading, fetchData]);

  // UpdateFilters
  const updateFilters = useCallback((newFilters: Partial<CPPDFilterParams>) => {
    const processedFilters = { ...newFilters };
    fetchData(processedFilters);
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    refetch,
    updateFilters,
    clienteId: getClienteId(),
    isInitialized
  };
};

export default useCPPDDashboard;